import { supabase } from './supabaseClient'

// Keep this aligned with the server's personal-mail classification. The server
// remains authoritative: a client-side choice never approves an enterprise.
const personalDomains = new Set(['gmail.com','gmail.fr','hotmail.com','hotmail.fr','outlook.com','icloud.com','live.com','msn.com','yahoo.com','yahoo.fr','bluewin.ch','gmx.ch','gmx.net','web.de','proton.me','protonmail.com','pm.me','mail.ch','hispeed.ch','sunrise.ch'])
export const isPersonalEmail = email => personalDomains.has(String(email || '').trim().toLowerCase().split('@')[1] || '')
export const normalizePostalCode = value => String(value || '').toUpperCase().replace(/[\s-]/g,'')
export const isPostalCompany = company => String(company?.zefix_verification_status || '').startsWith('postal_')
const postalError = (code,state) => Object.assign(new Error(code),{code,state})
const states = new Set(['pending','pending_generation','ready','sent','verified','expired','locked','revoked','email_required','none'])

// Deliberately whitelist the member-facing contract. Admin code/PDF fields can
// never enter component state through this helper.
export function postalMemberState(data) {
  const request=data?.request || null
  const status=data?.emailVerificationRequired===true?'email_required':String(request?.status || data?.status || 'none')
  return {
    status:states.has(status) ? status : 'none',
    expiresAt:typeof request?.expires_at==='string' ? request.expires_at : null,
    sentAt:typeof request?.sent_at==='string' ? request.sent_at : null,
    attemptsRemaining:Number.isInteger(request?.attempts_remaining) ? Math.max(0,request.attempts_remaining) : null,
    verificationStatus:typeof data?.companyVerificationStatus==='string' ? data.companyVerificationStatus : null,
    cooldownSeconds:Number.isFinite(Number(data?.cooldown_seconds)) ? Math.max(0,Number(data.cooldown_seconds)) : null,
  }
}

export async function postalMemberAction(action,{code,signal}={},client=supabase) {
  if (!['status','verify','requestEmailCode','verifyEmailCode'].includes(action)) throw postalError('invalid_action')
  if (action==='verifyEmailCode' && !/^\d{6}$/.test(String(code || '').trim())) throw postalError('email_invalid_code')
  const body={action,...(action==='verify'?{code:normalizePostalCode(code)}:action==='verifyEmailCode'?{code:String(code).trim()}:{})}
  const {data,error}=await client.functions.invoke('postal-verification',{body,signal,timeout:15000})
  let response=data
  if (error) {
    try {response=await error.context?.clone?.().json()} catch {response=null}
    const failure=postalError(response?.reason || response?.error || 'unavailable',response ? postalMemberState(response) : null)
    failure.retryAfterSeconds=Number(response?.retry_after_seconds)||null
    throw failure
  }
  if (!response || response.ok===false) throw postalError(response?.reason || response?.error || 'unavailable',response ? postalMemberState(response) : null)
  return postalMemberState(response)
}

export async function verifyPostalEmailCode({userId,email,token,signal},client=supabase) {
  if (!/^\d{6}$/.test(String(token || '').trim())) throw postalError('email_invalid_code')
  if (signal?.aborted) throw postalError('aborted')
  // Read only: dedicated postal email proof must never create or modify an
  // Auth session, trigger SIGNED_IN, or affect existing app/site logins.
  const {data:sessionData,error:sessionError}=await client.auth.getSession()
  if (sessionError) throw postalError('unavailable')
  const session=sessionData?.session
  if (!email || !session?.user?.email || session.user.id!==userId || String(session.user.email).trim().toLowerCase()!==String(email).trim().toLowerCase() || signal?.aborted) throw postalError('auth_changed')
  return postalMemberAction('verifyEmailCode',{code:token,signal},client)
}
